import type { EmailBouncesListBody } from '@/api/(admin)/email-bounces/types';
import type { EmailBouncesQueryInput } from '../-types';

/** The only column `post.list.ts` maps into its `ORDER BY`. */
const SORT_FIELD = 'created_at';

/**
 * Turns the table's state into the endpoint's body.
 *
 * The single conversion point, as elsewhere: the query key is the object this
 * returns, so the route loader (reading the URL) and the component's hook
 * (reading `useTable`) only share a cache entry if both derive it the same way.
 *
 * Three things it has to get right:
 *
 *  - **Page base.** No `-1` here, unlike every `/admin/*` list. This endpoint's
 *    `paginationValidationSchema` is `min(1).default(1)` and the handler does
 *    `(page - 1) * limit` itself, so subtracting again would skip a page.
 *  - **Sort.** `useTable` hands over `created_at:DESC`; the body wants the field
 *    and the direction apart, and the direction lower-cased. Any other field is
 *    dropped rather than sent — `sort` is a one-value enum and an unknown value
 *    fails the schema, taking the whole request with it.
 *  - **Filters.** Both are matched with `=`, not `LIKE`, so only a single value
 *    per column can ever match. Values are taken one at a time rather than
 *    comma-joined, which would send `a,b` and return nothing.
 */
export function toEmailBouncesBody(
  input: EmailBouncesQueryInput,
): EmailBouncesListBody {
  const [field, direction] = (input.sort ?? '').split(':');
  const order =
    field === SORT_FIELD && direction?.toUpperCase() === 'ASC' ? 'asc' : 'desc';

  const email = firstValue(input.filters?.email);
  const status = firstValue(input.filters?.status);

  return {
    page: Math.max(1, input.page),
    limit: input.limit,
    sort: SORT_FIELD,
    order,
    ...(email ? { email } : {}),
    ...(status ? { status } : {}),
  };
}

function firstValue(values: string[] | undefined): string | undefined {
  const value = values?.[0]?.trim();
  return value ? value : undefined;
}
